/**
 * Edit Match Teams Modal Component
 * Modale per la modifica delle coppie in un match
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { 
  Player, 
  CurrentPlayers, 
  EditMatchPairsData, 
  Substitution 
} from '../../types/match';
import { editMatchPairs } from '../../utils/api-client';

@customElement('pm-edit-match-pairs-modal')
export class EditMatchPairsModal extends LitElement {
  @property({ type: String, attribute: 'tournament-id' }) tournamentId = '';
  @property({ type: String, attribute: 'tournament-pk' }) tournamentPk = '';
  @property({ type: String, attribute: 'tournament-type' }) tournamentType = ''; // group_stage_social, round_robin, etc.
  @property({ type: Array, attribute: false }) allPlayers: Player[] = [];
  @property({ type: String, attribute: 'pairing-mode' }) pairingMode = ''; // round_robin_manual, etc.

  @state() private isOpen = false;
  @state() private isLoading = false;
  @state() private currentMatchId: number | null = null;
  @state() private currentGroup: string | null = null;
  @state() private currentRoundNumber: number | null = null;
  @state() private selectedPlayers = {
    side1_player1: '',
    side1_player2: '',
    side2_player1: '',
    side2_player2: ''
  };
  @state() private originalPlayers = {
    side1_player1: '',
    side1_player2: '',
    side2_player1: '',
    side2_player2: ''
  };
  @state() private groupedPlayers: Record<string, Player[]> = {};
  @state() private massiveSubstitution = false;
  @state() private isSaving = false;
  @state() private errorMessage = '';
  @state() private validationWarnings: any[] = [];
  @state() private playerSuggestions: any[] = [];

  static styles = css`
    :host {
      --modal-z-index: 1055;
      --backdrop-z-index: 1050;
    }

    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: var(--backdrop-z-index);
      display: none;
    }

    .modal-backdrop.show {
      display: block;
      animation: fadeIn 0.15s ease-in;
    }

    .modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: var(--modal-z-index);
      display: none;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .modal.show {
      display: block;
    }

    .modal-dialog {
      position: relative;
      width: auto;
      max-width: 800px;
      margin: 1.75rem auto;
      pointer-events: none;
    }

    .modal-content {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      pointer-events: auto;
      background-color: var(--bs-body-bg, #fff);
      background-clip: padding-box;
      border: 1px solid var(--bs-border-color, rgba(0,0,0,.2));
      border-radius: 0.5rem;
      outline: 0;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1rem;
      border-bottom: 1px solid var(--bs-border-color, #dee2e6);
      border-top-left-radius: calc(0.5rem - 1px);
      border-top-right-radius: calc(0.5rem - 1px);
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .modal-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.5;
    }

    .btn-close {
      box-sizing: content-box;
      width: 1em;
      height: 1em;
      padding: 0.25em 0.25em;
      color: white;
      background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
      border: 0;
      border-radius: 0.25rem;
      opacity: 0.8;
      cursor: pointer;
    }

    .btn-close:hover {
      opacity: 1;
    }

    .modal-body {
      position: relative;
      flex: 1 1 auto;
      padding: 1rem;
    }

    .modal-footer {
      display: flex;
      flex-wrap: wrap;
      flex-shrink: 0;
      align-items: center;
      justify-content: flex-end;
      padding: 0.75rem;
      border-top: 1px solid var(--bs-border-color, #dee2e6);
      border-bottom-right-radius: calc(0.5rem - 1px);
      border-bottom-left-radius: calc(0.5rem - 1px);
      gap: 0.5rem;
    }

    .alert {
      position: relative;
      padding: 0.75rem 1.25rem;
      margin-bottom: 1rem;
      border: 1px solid transparent;
      border-radius: 0.375rem;
    }

    .alert-info {
      color: #084298;
      background-color: #cfe2ff;
      border-color: #b6d4fe;
    }

    .alert-warning {
      color: #664d03;
      background-color: #fff3cd;
      border-color: #ffecb5;
    }

    .alert-danger {
      color: #842029;
      background-color: #f8d7da;
      border-color: #f5c2c7;
    }

    [data-bs-theme="dark"] .alert-info {
      color: #6ea8fe;
      background-color: #031633;
      border-color: #084298;
    }

    [data-bs-theme="dark"] .alert-warning {
      color: #ffda6a;
      background-color: #332701;
      border-color: #664d03;
    }

    [data-bs-theme="dark"] .alert-danger {
      color: #ea868f;
      background-color: #2c0b0e;
      border-color: #842029;
    }

    .card {
      position: relative;
      display: flex;
      flex-direction: column;
      min-width: 0;
      word-wrap: break-word;
      background-color: var(--bs-body-bg, #fff);
      background-clip: border-box;
      border: 1px solid var(--bs-border-color, rgba(0,0,0,.125));
      border-radius: 0.375rem;
      margin-bottom: 1rem;
    }

    .card-header {
      padding: 0.75rem 1.25rem;
      margin-bottom: 0;
      background-color: rgba(0,0,0,.03);
      border-bottom: 1px solid var(--bs-border-color, rgba(0,0,0,.125));
      border-top-left-radius: calc(0.375rem - 1px);
      border-top-right-radius: calc(0.375rem - 1px);
      font-weight: 600;
    }

    [data-bs-theme="dark"] .card-header {
      background-color: rgba(255,255,255,.05);
    }

    .card-body {
      flex: 1 1 auto;
      padding: 1.25rem 1.25rem;
    }

    .bg-light {
      background-color: rgba(0,0,0,.03) !important;
    }

    [data-bs-theme="dark"] .bg-light {
      background-color: rgba(255,255,255,.05) !important;
    }

    .form-check {
      display: block;
      min-height: 1.5rem;
      padding-left: 1.5em;
      margin-bottom: 0.125rem;
    }

    .form-check-input {
      width: 1em;
      height: 1em;
      margin-top: 0.25em;
      vertical-align: top;
      background-color: var(--bs-body-bg, #fff);
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      border: 1px solid rgba(0,0,0,.25);
      appearance: none;
      print-color-adjust: exact;
      cursor: pointer;
    }

    .form-check-input[type="checkbox"] {
      border-radius: 0.25em;
    }

    .form-check-input:checked {
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .form-check-input[type="checkbox"]:checked {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
    }

    .form-check-input:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .form-check-input:disabled:checked {
      background-color: #ffc107;
      border-color: #ffc107;
    }

    .form-check-label {
      cursor: pointer;
    }

    .form-check-input:disabled + .form-check-label {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .badge {
      display: inline-block;
      padding: 0.35em 0.65em;
      font-size: 0.75em;
      font-weight: 700;
      line-height: 1;
      color: #fff;
      text-align: center;
      white-space: nowrap;
      vertical-align: baseline;
      border-radius: 0.375rem;
    }

    .bg-warning {
      background-color: #ffc107;
    }

    .text-dark {
      color: #212529;
    }

    .ms-2 {
      margin-left: 0.5rem;
    }

    .form-label {
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
    }

    .form-select {
      display: block;
      width: 100%;
      padding: 0.375rem 2.25rem 0.375rem 0.75rem;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      color: var(--bs-body-color, #212529);
      background-color: var(--bs-body-bg, #fff);
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      background-size: 16px 12px;
      border: 1px solid var(--bs-border-color, #ced4da);
      border-radius: 0.375rem;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      appearance: none;
    }

    .form-select:focus {
      border-color: #86b7fe;
      outline: 0;
      box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
    }

    .form-select option[data-current-group] {
      font-weight: bold;
    }

    .row {
      display: flex;
      flex-wrap: wrap;
      margin-right: -0.75rem;
      margin-left: -0.75rem;
    }

    .col-md-6 {
      flex: 0 0 auto;
      width: 50%;
      padding-right: 0.75rem;
      padding-left: 0.75rem;
    }

    .mb-3 {
      margin-bottom: 1rem !important;
    }

    .mb-0 {
      margin-bottom: 0 !important;
    }

    .mt-1 {
      margin-top: 0.25rem !important;
    }

    .mt-2 {
      margin-top: 0.5rem !important;
    }

    .me-1 {
      margin-right: 0.25rem !important;
    }

    .me-2 {
      margin-right: 0.5rem !important;
    }

    .d-block {
      display: block !important;
    }

    .text-muted {
      color: #6c757d !important;
    }

    .text-danger {
      color: #dc3545 !important;
    }

    .btn {
      display: inline-block;
      font-weight: 400;
      line-height: 1.5;
      color: #212529;
      text-align: center;
      text-decoration: none;
      vertical-align: middle;
      cursor: pointer;
      user-select: none;
      background-color: transparent;
      border: 1px solid transparent;
      padding: 0.375rem 0.75rem;
      font-size: 1rem;
      border-radius: 0.375rem;
      transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }

    .btn:hover {
      color: #212529;
    }

    .btn:disabled {
      pointer-events: none;
      opacity: 0.65;
    }

    .btn-primary {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .btn-primary:hover {
      color: #fff;
      background-color: #0b5ed7;
      border-color: #0a58ca;
    }

    .btn-primary:disabled {
      color: #fff;
      background-color: #0d6efd;
      border-color: #0d6efd;
    }

    .btn-secondary {
      color: #fff;
      background-color: #6c757d;
      border-color: #6c757d;
    }

    .btn-secondary:hover {
      color: #fff;
      background-color: #5c636a;
      border-color: #565e64;
    }

    .icon {
      display: inline-block;
      width: 1em;
      height: 1em;
      vertical-align: -0.125em;
    }

    .spinner {
      animation: fa-spin 2s infinite linear;
    }

    @keyframes fa-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    small {
      font-size: 0.875rem;
    }

    ul {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    li {
      margin: 0.25rem 0;
    }

    strong {
      font-weight: 600;
    }

    #massiveSubstitutionHelp {
      display: none;
      transition: all 0.3s ease;
    }

    #massiveSubstitutionHelp.show {
      display: block;
    }

    /* Warnings e suggerimenti per Round Robin Manuale */
    .validation-warnings {
      margin: 1rem 0;
      padding: 0;
    }

    .warning-item {
      display: flex;
      align-items: start;
      padding: 0.75rem;
      margin-bottom: 0.5rem;
      border-radius: 0.375rem;
      font-size: 0.9rem;
    }

    .warning-item.warning {
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      color: #664d03;
    }

    .warning-item.info {
      background-color: #cfe2ff;
      border-left: 4px solid #0dcaf0;
      color: #084298;
    }

    .warning-icon {
      margin-right: 0.5rem;
      font-size: 1.2rem;
    }

    .suggestions-section {
      margin-top: 1rem;
      padding: 1rem;
      background-color: #d1e7dd;
      border-radius: 0.375rem;
      border-left: 4px solid #198754;
    }

    .suggestions-title {
      font-weight: 600;
      color: #0f5132;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .suggestion-item {
      padding: 0.5rem;
      margin-bottom: 0.5rem;
      background-color: white;
      border-radius: 0.25rem;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .suggestion-item:hover {
      background-color: #f8f9fa;
    }

    .suggestion-players {
      font-weight: 500;
      color: #212529;
    }

    .suggestion-reason {
      font-size: 0.85rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    @media (max-width: 768px) {
      .col-md-6 {
        width: 100%;
      }

      .modal-dialog {
        margin: 0.5rem;
      }
    }
  `;

  // SVG Icons
  private svgEdit() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160V416c0 53 43 96 96 96H352c53 0 96-43 96-96V320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H96z"/></svg>`;
  }

  private svgInfo() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>`;
  }

  private svgSync() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z"/></svg>`;
  }

  private svgUsers() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" fill="currentColor"><path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0H21.3C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/></svg>`;
  }

  private svgWarning() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/></svg>`;
  }

  private svgTimes() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>`;
  }

  private svgSave() {
    return html`<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor"><path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V173.3c0-17-6.7-33.3-18.7-45.3L352 50.7C340 38.7 323.7 32 306.7 32H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V128zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`;
  }

  private svgSpinner() {
    return html`<svg class="icon spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M304 48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zm0 416a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM48 304a48 48 0 1 0 0-96 48 48 0 1 0 0 96zm464-48a48 48 0 1 0 -96 0 48 48 0 1 0 96 0zM142.9 437A48 48 0 1 0 75 369.1 48 48 0 1 0 142.9 437zm0-294.2A48 48 0 1 0 75 75a48 48 0 1 0 67.9 67.9zM369.1 437A48 48 0 1 0 437 369.1 48 48 0 1 0 369.1 437z"/></svg>`;
  }

  /**
   * Metodo pubblico per aprire il modale
   */
  async open(matchId: number, group: string, currentPlayers: CurrentPlayers, roundNumber?: number) {
    this.currentMatchId = matchId;
    this.currentGroup = group;
    this.currentRoundNumber = roundNumber || null;
    this.isOpen = true;
    this.errorMessage = '';
    this.massiveSubstitution = false;
    this.isLoading = true;
    this.validationWarnings = [];
    this.playerSuggestions = [];
    
    // Salva i giocatori originali e impostali come selezionati
    this.originalPlayers = {
      side1_player1: currentPlayers.side1_player1_id || '',
      side1_player2: currentPlayers.side1_player2_id || '',
      side2_player1: currentPlayers.side2_player1_id || '',
      side2_player2: currentPlayers.side2_player2_id || ''
    };
    
    this.selectedPlayers = { ...this.originalPlayers };

    // Carica la lista dei giocatori disponibili dal torneo
    await this.loadAvailablePlayers();

    // Se è round robin manuale, esegui validazione iniziale
    if (this.pairingMode === 'round_robin_manual') {
      await this.validateManualRoundRobin();
    }
  }

  /**
   * Carica i giocatori disponibili per il torneo
   */
  private async loadAvailablePlayers() {
    try {
      // Se i giocatori sono già stati passati come prop, usali
      if (this.allPlayers && this.allPlayers.length > 0) {
        console.log('🎾 Using pre-loaded players:', this.allPlayers.length);
        this.isLoading = false;
        return;
      }

      // Altrimenti, cerca nel DOM se ci sono dati players
      const modalElement = this.closest('pm-edit-match-pairs-modal');
      const playersDataElement = document.querySelector('[data-tournament-players]');
      
      if (playersDataElement) {
        const playersData = playersDataElement.getAttribute('data-tournament-players');
        if (playersData) {
          const data = JSON.parse(playersData);
          this.allPlayers = data.players || [];
          this.groupedPlayers = data.grouped_players || {};
          
          console.log('🎾 Players loaded from DOM:', {
            total: this.allPlayers.length,
            groups: Object.keys(this.groupedPlayers).length
          });
          
          this.isLoading = false;
          return;
        }
      }

      // Fallback: non ci sono giocatori
      console.warn('⚠️ No players data found');
      this.allPlayers = [];
      this.groupedPlayers = {};

    } catch (error) {
      console.error('Errore nel caricamento dei giocatori:', error);
      this.errorMessage = 'Impossibile caricare la lista dei giocatori. Riprova.';
      this.allPlayers = [];
      this.groupedPlayers = {};
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Metodo pubblico per chiudere il modale
   */
  close() {
    this.isOpen = false;
    this.currentMatchId = null;
    this.currentGroup = null;
    this.errorMessage = '';
    this.massiveSubstitution = false;
    this.isSaving = false;
    this.selectedPlayers = {
      side1_player1: '',
      side1_player2: '',
      side2_player1: '',
      side2_player2: ''
    };
    
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  private handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      this.close();
    }
  }

  private handleMassiveSubstitutionChange(e: Event) {
    const checkbox = e.target as HTMLInputElement;
    this.massiveSubstitution = checkbox.checked;
  }

  private handlePlayerSelect(team: string, position: string, e: Event) {
    const select = e.target as HTMLSelectElement;
    const key = `${team}_${position}` as keyof typeof this.selectedPlayers;
    const newPlayerId = select.value;
    const oldPlayerId = this.selectedPlayers[key];

    // PRIMA aggiorna selectedPlayers
    this.selectedPlayers = {
      ...this.selectedPlayers,
      [key]: newPlayerId
    };

    // POI controlla se serve auto-abilitare la sostituzione massiva
    // (group_stage_social + nuovo giocatore appartiene a un gruppo)
    if (this.tournamentType === 'group_stage_social') {
      // Controlla se il NUOVO giocatore selezionato appartiene a un gruppo
      if (newPlayerId !== oldPlayerId && newPlayerId && this.isPlayerInAnyGroup(newPlayerId)) {
        this.massiveSubstitution = true;
      }
    }

    // Validazione real-time per Round Robin Manuale
    if (this.pairingMode === 'round_robin_manual') {
      this.validateManualRoundRobin();
    }

    // Forza re-render per aggiornare UI
    this.requestUpdate();
  }

  private validateTeamSelection(): { valid: boolean; message?: string } {
    const { side1_player1, side1_player2, side2_player1, side2_player2 } = this.selectedPlayers;
    const selectedPlayers = [side1_player1, side1_player2, side2_player1, side2_player2].filter(id => id !== '');

    // Verifica che tutti i giocatori siano selezionati
    if (selectedPlayers.length !== 4) {
      return { valid: false, message: 'Devi selezionare tutti e 4 i giocatori.' };
    }

    // Verifica che non ci siano duplicati
    const uniquePlayers = new Set(selectedPlayers);
    if (uniquePlayers.size !== 4) {
      return { valid: false, message: 'Non puoi selezionare lo stesso giocatore più volte.' };
    }

    // Verifica che i giocatori nella stessa coppia siano diversi
    if (side1_player1 === side1_player2) {
      return { valid: false, message: 'I due giocatori della Coppia 1 devono essere diversi.' };
    }
    if (side2_player1 === side2_player2) {
      return { valid: false, message: 'I due giocatori della Coppia 2 devono essere diversi.' };
    }

    return { valid: true };
  }

  /**
   * Valida la selezione tramite API per Round Robin Manuale
   * Mostra warnings e suggerimenti
   */
  private async validateManualRoundRobin() {
    if (this.pairingMode !== 'round_robin_manual') {
      return; // Solo per round robin manuale
    }

    const { side1_player1, side1_player2, side2_player1, side2_player2 } = this.selectedPlayers;
    
    // Se non ci sono giocatori selezionati, resetta
    if (!side1_player1 && !side1_player2 && !side2_player1 && !side2_player2) {
      this.validationWarnings = [];
      this.playerSuggestions = [];
      return;
    }

    try {
      const response = await fetch('/simulations/api/manual-round-robin/validate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.getCSRFToken()
        },
        body: JSON.stringify({
          tournament_id: this.getTournamentId(),
          match_id: this.currentMatchId,
          round_number: this.currentRoundNumber,
          side1_player1_id: side1_player1 ? parseInt(side1_player1) : null,
          side1_player2_id: side1_player2 ? parseInt(side1_player2) : null,
          side2_player1_id: side2_player1 ? parseInt(side2_player1) : null,
          side2_player2_id: side2_player2 ? parseInt(side2_player2) : null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.validationWarnings = data.warnings || [];
        this.playerSuggestions = data.suggestions || [];
      }
    } catch (error) {
      console.error('Errore validazione manuale:', error);
    }
  }

  private getCSRFToken(): string {
    const cookie = document.cookie.split('; ').find(row => row.startsWith('csrftoken='));
    return cookie ? cookie.split('=')[1] : '';
  }

  private findPlayerGroup(playerId: string): string | null {
    const player = this.allPlayers.find(p => p.id.toString() === playerId);
    return player ? player.group : null;
  }

  private findPlayerName(playerId: string): string | null {
    const player = this.allPlayers.find(p => p.id.toString() === playerId);
    return player ? player.name : null;
  }

  /**
   * Applica un suggerimento selezionato dall'utente
   */
  private applySuggestion(suggestion: any) {
    // Aggiorna gli avversari (side2) con il suggerimento
    this.selectedPlayers = {
      ...this.selectedPlayers,
      side2_player1: suggestion.player1_id?.toString() || '',
      side2_player2: suggestion.player2_id?.toString() || ''
    };

    // Resetta suggerimenti dopo l'applicazione
    this.playerSuggestions = [];
    
    // Rivalidata per aggiornare warnings
    if (this.pairingMode === 'round_robin_manual') {
      this.validateManualRoundRobin();
    }

    this.requestUpdate();
  }

  private findSubstitutions(): Substitution[] {
    const substitutions: Substitution[] = [];
    const selectedValues = Object.values(this.selectedPlayers);
    const originalValues = Object.values(this.originalPlayers);

    selectedValues.forEach((newPlayerId, index) => {
      const oldPlayerId = originalValues[index];

      if (newPlayerId !== oldPlayerId && newPlayerId && oldPlayerId) {
        const oldPlayerGroup = this.findPlayerGroup(oldPlayerId);
        const newPlayerGroup = this.findPlayerGroup(newPlayerId);

        // Se i gruppi sono diversi, è una sostituzione cross-gruppo
        if (oldPlayerGroup !== newPlayerGroup && oldPlayerGroup && newPlayerGroup) {
          substitutions.push({
            oldPlayerId,
            oldPlayerName: this.findPlayerName(oldPlayerId) || '',
            oldPlayerGroup,
            newPlayerId,
            newPlayerName: this.findPlayerName(newPlayerId) || '',
            newPlayerGroup
          });
        }
      }
    });

    return substitutions;
  }

  private buildConfirmMessage(substitutions: Substitution[]): string {
    let message = '⚠️ SOSTITUZIONE MASSIVA - Conferma le seguenti modifiche:\n\n';

    substitutions.forEach(sub => {
      message += `🔄 ${sub.oldPlayerName} (Gruppo ${sub.oldPlayerGroup}) ➜ ${sub.newPlayerName} (Gruppo ${sub.newPlayerGroup})\n`;
      message += `   • ${sub.newPlayerName} sostituirà ${sub.oldPlayerName} in TUTTI i match del Gruppo ${sub.oldPlayerGroup}\n`;
      message += `   • ${sub.oldPlayerName} verrà spostato nel Gruppo ${sub.newPlayerGroup}\n\n`;
    });

    message += 'Vuoi procedere con questa sostituzione massiva?';
    return message;
  }

  /**
   * Ottiene il tournament ID con fallback intelligenti
   */
  private getTournamentId(): string {
    // Priority 1: tournamentPk property
    if (this.tournamentPk && this.tournamentPk !== '') {
      return this.tournamentPk;
    }

    // Priority 2: tournamentId property
    if (this.tournamentId && this.tournamentId !== '') {
      return this.tournamentId;
    }

    // Priority 3: Extract from URL
    const pathMatch = window.location.pathname.match(/\/simulations\/tournaments\/(\d+)\//);
    if (pathMatch && pathMatch[1]) {
      console.log('🔍 Tournament ID extracted from URL:', pathMatch[1]);
      return pathMatch[1];
    }

    // Priority 4: Look for data attribute in DOM
    const tournamentElement = document.querySelector('[data-tournament-id]') as HTMLElement;
    if (tournamentElement && tournamentElement.dataset.tournamentId) {
      console.log('🔍 Tournament ID found in DOM:', tournamentElement.dataset.tournamentId);
      return tournamentElement.dataset.tournamentId;
    }

    return '';
  }

  /**
   * Verifica se un giocatore appartiene a qualche gruppo
   */
  private isPlayerInAnyGroup(playerId: string): boolean {
    const player = this.allPlayers.find(p => p.id.toString() === playerId);
    return player ? (player.group !== null && player.group !== undefined && player.group !== '') : false;
  }

  /**
   * Verifica se la sostituzione deve essere automaticamente massiva
   * (group_stage_social + almeno un giocatore cambiato appartiene a un gruppo)
   */
  private shouldAutoEnableMassiveSubstitution(): boolean {
    // Solo per group_stage_social
    if (this.tournamentType !== 'group_stage_social') {
      return false;
    }

    // Trova i giocatori che sono stati cambiati
    const keys = Object.keys(this.selectedPlayers) as Array<keyof typeof this.selectedPlayers>;
    
    for (const key of keys) {
      const newPlayerId = this.selectedPlayers[key];
      const originalPlayerId = this.originalPlayers[key];

      // Se il giocatore è cambiato e il NUOVO giocatore appartiene a un gruppo
      if (newPlayerId !== originalPlayerId && newPlayerId && originalPlayerId) {
        if (this.isPlayerInAnyGroup(newPlayerId)) {
          return true; // Deve essere massiva e obbligatoria
        }
      }
    }

    return false; // Nessun giocatore con gruppo cambiato → può essere singolo match
  }

  /**
   * Verifica se la checkbox di sostituzione massiva deve essere disabled
   */
  private shouldDisableMassiveSubstitutionCheckbox(): boolean {
    // Per group_stage_social: disabled se almeno un giocatore cambiato appartiene a un gruppo
    return this.tournamentType === 'group_stage_social' && this.shouldAutoEnableMassiveSubstitution();
  }

  /**
   * Ottiene il messaggio di help per la sostituzione massiva
   */
  private getMassiveSubstitutionHelpMessage() {
    if (this.tournamentType === 'group_stage_social') {
      if (this.shouldAutoEnableMassiveSubstitution()) {
        return html`
          <strong>Sostituzione massiva obbligatoria per i gruppi</strong>
          <ul class="mb-0 mt-1">
            <li>Stai sostituendo un giocatore che appartiene a un gruppo</li>
            <li>La sostituzione verrà applicata automaticamente a <strong>tutti i match</strong> del suo gruppo</li>
            <li>Questo mantiene la coerenza del torneo a gironi</li>
          </ul>
        `;
      } else {
        return html`
          Il giocatore selezionato non appartiene a nessun gruppo del torneo.
          Puoi scegliere se applicare la sostituzione solo a questo match o a più match.
        `;
      }
    }

    if (this.tournamentType === 'round_robin') {
      return html`
        Quando attivi questa opzione e sostituisci un giocatore:
        <ul class="mb-0 mt-1">
          <li>Il giocatore sostituito verrà rimosso da TUTTI i match del girone</li>
          <li>Il nuovo giocatore prenderà il suo posto in TUTTI i match</li>
          <li>I due giocatori scambieranno automaticamente i gruppi</li>
        </ul>
        <strong class="text-danger">Se non attivi questa opzione, modificherai solo questo match.</strong>
      `;
    }

    // Default message
    return html`
      Attiva questa opzione per applicare la sostituzione a tutti i match rilevanti del torneo.
    `;
  }

  private async handleSave() {
    // Validazione
    const validation = this.validateTeamSelection();
    if (!validation.valid) {
      this.errorMessage = validation.message || '';
      return;
    }

    // Se sostituzione massiva è attiva, verifica se ci sono sostituzioni cross-gruppo
    if (this.massiveSubstitution) {
      const substitutions = this.findSubstitutions();

      if (substitutions.length > 0) {
        const confirmMessage = this.buildConfirmMessage(substitutions);
        if (!confirm(confirmMessage)) {
          return; // L'utente ha annullato
        }
      }
    }

    // Preparazione dati
    const data: EditMatchPairsData = {
      match_id: this.currentMatchId!,
      side1_player1: this.selectedPlayers.side1_player1,
      side1_player2: this.selectedPlayers.side1_player2,
      side2_player1: this.selectedPlayers.side2_player1,
      side2_player2: this.selectedPlayers.side2_player2,
      massive_substitution: this.massiveSubstitution,
      current_group: this.currentGroup!
    };

    this.isSaving = true;
    this.errorMessage = '';

    // Ottieni tournament ID con fallback intelligenti
    const tournamentId = this.getTournamentId();

    // Debug logging
    console.log('🔍 Edit teams debug:', {
      tournamentIdProp: this.tournamentId,
      tournamentPkProp: this.tournamentPk,
      resolvedTournamentId: tournamentId,
      matchId: this.currentMatchId,
      group: this.currentGroup,
      roundNumber: this.currentRoundNumber,
      selectedPlayers: this.selectedPlayers,
      data: data,
      url: window.location.pathname
    });

    // Verifica che abbiamo un tournament ID valido
    if (!tournamentId) {
      this.errorMessage = 'Impossibile determinare l\'ID del torneo. Ricarica la pagina e riprova.';
      this.isSaving = false;
      return;
    }

    try {
      const result = await editMatchPairs(tournamentId, data);

      if (result.success) {
        // Mostra messaggio di successo con dettagli
        let successMessage = 'Coppie modificate con successo!';
        if (result.massive_substitution_applied && result.affected_matches && result.affected_matches > 0) {
          successMessage += `\n\n✅ Sostituzione massiva completata:\n${result.affected_matches} match modificati`;
        }
        alert(successMessage);

        this.close();
        
        // Emetti evento di aggiornamento
        this.dispatchEvent(new CustomEvent('pairs-updated', {
          detail: { matchId: this.currentMatchId },
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Impossibile modificare le coppie.';
      }
    } catch (error) {
      console.error('Edit teams error:', error);
      this.errorMessage = error instanceof Error ? error.message : 'Errore di connessione. Riprova.';
    } finally {
      this.isSaving = false;
    }
  }

  private renderPlayerOptions(currentGroup: string, selectedPlayerId?: string) {
    // Ordina i giocatori: prima quelli del gruppo corrente, poi gli altri alfabeticamente
    const sortedPlayers = [...this.allPlayers].sort((a, b) => {
      const aIsCurrentGroup = a.group === currentGroup;
      const bIsCurrentGroup = b.group === currentGroup;
      
      // Priorità: giocatori del gruppo corrente prima
      if (aIsCurrentGroup && !bIsCurrentGroup) return -1;
      if (!aIsCurrentGroup && bIsCurrentGroup) return 1;
      
      // Poi ordina alfabeticamente
      return a.name.localeCompare(b.name);
    });

    return html`
      <option value="" ?selected=${!selectedPlayerId}>Seleziona giocatore...</option>
      ${sortedPlayers.map(player => {
        const isCurrentGroup = player.group === currentGroup;
        const isSelected = selectedPlayerId && player.id.toString() === selectedPlayerId;
        
        // Gestione label con gruppo o "Nessun gruppo" se sparito dal bracket
        let groupLabel = '';
        if (player.group) {
          groupLabel = `Gruppo ${player.group}`;
        } else {
          groupLabel = '⚠️ Nessun gruppo';
        }
        
        const label = `${player.name} (${player.skill}) - ${groupLabel}${isCurrentGroup ? ' ⭐' : ''}`;
        
        return html`
          <option 
            value="${player.id}"
            ?selected=${isSelected}
            ?data-current-group=${isCurrentGroup}
            style="${isCurrentGroup ? 'font-weight: bold;' : ''} ${!player.group ? 'color: #dc3545;' : ''}"
          >
            ${label}
          </option>
        `;
      })}
    `;
  }

  render() {
    if (!this.isOpen) return html``;

    return html`
      <div class="modal-backdrop show" @click=${this.handleBackdropClick}></div>
      <div class="modal show" tabindex="-1" @click=${this.handleBackdropClick}>
        <div class="modal-dialog">
          <div class="modal-content" @click=${(e: Event) => e.stopPropagation()}>
            <div class="modal-header">
              <h5 class="modal-title">
                <span class="me-2">${this.svgEdit()}</span>Modifica Coppie Match - Gruppo ${this.currentGroup}
              </h5>
              <button type="button" class="btn-close" @click=${this.close} aria-label="Close"></button>
            </div>
            
            <div class="modal-body">
              ${this.errorMessage ? html`
                <div class="alert alert-danger">
                  <span class="me-2">${this.svgWarning()}</span>
                  <strong>Errore:</strong> ${this.errorMessage}
                </div>
              ` : ''}

              <div class="alert alert-info">
                <span class="me-2">${this.svgInfo()}</span>
                <strong>Attenzione:</strong> La modifica delle coppie influenzerà le statistiche del torneo.<br>
                • Puoi selezionare <strong>qualsiasi giocatore iscritto al torneo</strong><br>
                • I giocatori del gruppo corrente sono evidenziati con ⭐<br>
                • I giocatori senza gruppo (spariti dal bracket) sono marcati con ⚠️
              </div>

              <!-- Opzioni di sostituzione -->
              <div class="card bg-light mb-3">
                <div class="card-body">
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="massiveSubstitution"
                      ?checked=${this.massiveSubstitution}
                      ?disabled=${this.shouldDisableMassiveSubstitutionCheckbox()}
                      @change=${this.handleMassiveSubstitutionChange}
                    >
                    <label class="form-check-label" for="massiveSubstitution">
                      <strong><span class="me-2">${this.svgSync()}</span>Sostituzione Massiva</strong>
                      ${this.shouldDisableMassiveSubstitutionCheckbox() ? html`
                        <span class="badge bg-warning text-dark ms-2">Obbligatoria</span>
                      ` : ''}
                    </label>
                  </div>
                  <small class="text-muted d-block mt-2" id="massiveSubstitutionHelp">
                    ${this.getMassiveSubstitutionHelpMessage()}
                  </small>
                </div>
              </div>

              <!-- Coppia 1 -->
              <div class="card mb-3">
                <div class="card-header">
                  <span class="me-2">${this.svgUsers()}</span>Coppia 1
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label for="side1_player1" class="form-label">Giocatore 1 *</label>
                      <select 
                        class="form-select" 
                        id="side1_player1"
                        @change=${(e: Event) => this.handlePlayerSelect('side1', 'player1', e)}
                        required
                      >
                        ${this.renderPlayerOptions(this.currentGroup || '', this.selectedPlayers.side1_player1)}
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label for="side1_player2" class="form-label">Giocatore 2 *</label>
                      <select 
                        class="form-select" 
                        id="side1_player2"
                        @change=${(e: Event) => this.handlePlayerSelect('side1', 'player2', e)}
                        required
                      >
                        ${this.renderPlayerOptions(this.currentGroup || '', this.selectedPlayers.side1_player2)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Coppia 2 -->
              <div class="card mb-3">
                <div class="card-header">
                  <span class="me-2">${this.svgUsers()}</span>Coppia 2
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label for="side2_player1" class="form-label">Giocatore 1 *</label>
                      <select 
                        class="form-select" 
                        id="side2_player1"
                        @change=${(e: Event) => this.handlePlayerSelect('side2', 'player1', e)}
                        required
                      >
                        ${this.renderPlayerOptions(this.currentGroup || '', this.selectedPlayers.side2_player1)}
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label for="side2_player2" class="form-label">Giocatore 2 *</label>
                      <select 
                        class="form-select" 
                        id="side2_player2"
                        @change=${(e: Event) => this.handlePlayerSelect('side2', 'player2', e)}
                        required
                      >
                        ${this.renderPlayerOptions(this.currentGroup || '', this.selectedPlayers.side2_player2)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Warnings e Suggerimenti per Round Robin Manuale -->
              ${this.pairingMode === 'round_robin_manual' ? html`
                ${this.validationWarnings.length > 0 ? html`
                  <div class="validation-warnings">
                    ${this.validationWarnings.map(warning => html`
                      <div class="warning-item ${warning.severity}">
                        <span class="warning-icon">${warning.severity === 'warning' ? '⚠️' : 'ℹ️'}</span>
                        <span>${warning.message}</span>
                      </div>
                    `)}
                  </div>
                ` : ''}

                ${this.playerSuggestions.length > 0 ? html`
                  <div class="suggestions-section">
                    <div class="suggestions-title">
                      💡 Suggerimenti avversari disponibili
                    </div>
                    ${this.playerSuggestions.slice(0, 5).map(suggestion => html`
                      <div 
                        class="suggestion-item"
                        @click=${() => this.applySuggestion(suggestion)}
                      >
                        <div class="suggestion-players">
                          ${suggestion.player1_name} ${suggestion.player2_name ? `/ ${suggestion.player2_name}` : ''}
                        </div>
                        <div class="suggestion-reason">${suggestion.reason}</div>
                      </div>
                    `)}
                  </div>
                ` : ''}
              ` : ''}

              <div class="alert alert-warning">
                <span class="me-2">${this.svgWarning()}</span>
                <strong>Nota:</strong> Non puoi modificare match già completati. Se necessario, resetta prima il punteggio del match.
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click=${this.close}>
                <span class="me-2">${this.svgTimes()}</span>Annulla
              </button>
              <button 
                type="button" 
                class="btn btn-primary" 
                @click=${this.handleSave}
                ?disabled=${this.isSaving}
              >
                ${this.isSaving ? html`
                  <span class="me-2">${this.svgSpinner()}</span>Salvataggio...
                ` : html`
                  <span class="me-2">${this.svgSave()}</span>Salva Modifiche
                `}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-edit-match-pairs-modal': EditMatchPairsModal;
  }
}
